/**
 * Inbox API Service
 *
 * API methods for inbox operations including listing messages, marking as read, and archiving.
 */

import apiClient from './client';
import type { ApiSuccessResponse } from '../types/api.types';
import type {
  InboxMessageSanitized,
  InboxListQuery,
  InboxListResponse,
  UnreadCountResponse,
  BulkActionType,
  BulkActionResult,
} from '../types/inbox.types';

// ============================================================================
// RESPONSE WRAPPER TYPES
// ============================================================================

interface MarkAsReadResponse {
  markedCount: number;
}

// ============================================================================
// INBOX SERVICE
// ============================================================================

export const inboxService = {
  /**
   * List inbox messages for the current user
   */
  listMessages: async (
    tenantId: string,
    userId: string,
    query?: InboxListQuery
  ): Promise<InboxListResponse> => {
    const response = await apiClient.get<ApiSuccessResponse<InboxMessageSanitized[]>>(
      `/tenants/${tenantId}/users/${userId}/inbox`,
      { params: query }
    );
    // The response includes pagination in the response body
    const pagination = (response.data as any).pagination;
    return {
      items: response.data.data,
      count: pagination?.count || response.data.data.length,
      hasMore: pagination?.hasMore || false,
      cursor: pagination?.cursor,
    };
  },

  /**
   * Get unread message count for badge display
   */
  getUnreadCount: async (
    tenantId: string,
    userId: string
  ): Promise<number> => {
    const response = await apiClient.get<ApiSuccessResponse<UnreadCountResponse>>(
      `/tenants/${tenantId}/users/${userId}/inbox/unread-count`
    );
    return response.data.data.unreadCount;
  },

  /**
   * Mark a single message as read
   */
  markAsRead: async (
    tenantId: string,
    userId: string,
    messageId: string
  ): Promise<InboxMessageSanitized> => {
    const response = await apiClient.post<ApiSuccessResponse<InboxMessageSanitized>>(
      `/tenants/${tenantId}/users/${userId}/inbox/${messageId}/read`
    );
    return response.data.data;
  },

  /**
   * Mark all messages as read
   */
  markAllAsRead: async (
    tenantId: string,
    userId: string
  ): Promise<number> => {
    const response = await apiClient.post<ApiSuccessResponse<MarkAsReadResponse>>(
      `/tenants/${tenantId}/users/${userId}/inbox/read-all`
    );
    return response.data.data.markedCount;
  },

  /**
   * Archive a message
   */
  archiveMessage: async (
    tenantId: string,
    userId: string,
    messageId: string
  ): Promise<InboxMessageSanitized> => {
    const response = await apiClient.post<ApiSuccessResponse<InboxMessageSanitized>>(
      `/tenants/${tenantId}/users/${userId}/inbox/${messageId}/archive`
    );
    return response.data.data;
  },

  /**
   * Unarchive a message (move back to inbox)
   */
  unarchiveMessage: async (
    tenantId: string,
    userId: string,
    messageId: string
  ): Promise<InboxMessageSanitized> => {
    const response = await apiClient.post<ApiSuccessResponse<InboxMessageSanitized>>(
      `/tenants/${tenantId}/users/${userId}/inbox/${messageId}/unarchive`
    );
    return response.data.data;
  },

  /**
   * Soft delete a message (moves to deleted view with 30-day TTL)
   */
  softDeleteMessage: async (
    tenantId: string,
    userId: string,
    messageId: string
  ): Promise<InboxMessageSanitized> => {
    const response = await apiClient.post<ApiSuccessResponse<InboxMessageSanitized>>(
      `/tenants/${tenantId}/users/${userId}/inbox/${messageId}/delete`
    );
    return response.data.data;
  },

  /**
   * Restore a soft-deleted message (moves back to inbox)
   */
  restoreMessage: async (
    tenantId: string,
    userId: string,
    messageId: string
  ): Promise<InboxMessageSanitized> => {
    const response = await apiClient.post<ApiSuccessResponse<InboxMessageSanitized>>(
      `/tenants/${tenantId}/users/${userId}/inbox/${messageId}/restore`
    );
    return response.data.data;
  },

  /**
   * Permanently delete a message (only works on soft-deleted messages)
   */
  permanentDeleteMessage: async (
    tenantId: string,
    userId: string,
    messageId: string
  ): Promise<void> => {
    await apiClient.delete<ApiSuccessResponse<{ message: string }>>(
      `/tenants/${tenantId}/users/${userId}/inbox/${messageId}`
    );
  },

  /**
   * Perform a bulk action on multiple messages
   */
  bulkAction: async (
    tenantId: string,
    userId: string,
    action: BulkActionType,
    messageIds: string[]
  ): Promise<BulkActionResult> => {
    const response = await apiClient.post<ApiSuccessResponse<BulkActionResult>>(
      `/tenants/${tenantId}/users/${userId}/inbox/bulk`,
      { action, messageIds }
    );
    return response.data.data;
  },
};

export default inboxService;
